/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { EDSProvider } from "@eds-web/react/EDSProvider";
import { themeClass } from "@eds-web/react/theme";
import { I18NProvider } from "@epic-mw/i18n";
import tokens from "@epic-tokens/eds-web/tokens.js";
import { DecoratorFunction, PartialStoryFn, StoryContext } from "storybook/internal/types";
import TimeAgo from "javascript-time-ago";
import enTimeAgo from "javascript-time-ago/locale/en";
import jaTimeAgo from "javascript-time-ago/locale/ja";
import frTimeAgo from "javascript-time-ago/locale/fr";
import { cx } from "@panda/css";
import React, { HTMLAttributes } from "react";
import { ReactRenderer } from "@storybook/react-vite";
import { LocationProvider } from "@epic-mw/app-context";
import { AppRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { withScreenshot as withScreenshot$1 } from "storycap";
import { AccountProvider } from "@cosmos/next-hooks";

export function backgroundColorDecorator<T>(): DecoratorFunction<ReactRenderer, T> {
	const Decorator = (
		Story: PartialStoryFn<ReactRenderer, T>,
		context: StoryContext<ReactRenderer, T>,
	): React.JSX.Element => (
		<div className="backgroundColorDecorator" style={{ background: tokens.color.background.default }}>
			<Story {...context} />
		</div>
	);
	Decorator.displayName = "backgroundColorDecorator";
	return Decorator;
}

export function paddingDecorator<T>(): DecoratorFunction<ReactRenderer, T> {
	const Decorator = (
		Story: PartialStoryFn<ReactRenderer, T>,
		context: StoryContext<ReactRenderer, T>,
	): React.JSX.Element => (
		<div className="paddingDecorator" style={{ padding: "2rem" }}>
			<Story {...context} />
		</div>
	);
	Decorator.displayName = "paddingDecorator";
	return Decorator;
}

export function edsProviderDecorator<T>(): DecoratorFunction<ReactRenderer, T> {
	const Decorator = (
		Story: PartialStoryFn<ReactRenderer, T>,
		context: StoryContext<ReactRenderer, T>,
	): React.JSX.Element => (
		<div className={cx("edsProviderDecorator", themeClass)} lang={"en"} dir={"ltr"}>
			<EDSProvider dir={"ltr"}>
				<Story {...context} />
			</EDSProvider>
		</div>
	);
	Decorator.displayName = "edsProviderDecorator";
	return Decorator;
}

export function boundDecoratorFn(
	width: string,
	height?: string,
	style?: HTMLAttributes<HTMLDivElement>["style"],
): DecoratorFunction<ReactRenderer> {
	const Decorator = (Story: PartialStoryFn<ReactRenderer>, context: StoryContext<ReactRenderer>): React.JSX.Element => (
		<div className="boundDecorator" style={{ ...style, width, height }}>
			<Story {...context} />
		</div>
	);
	Decorator.displayName = "boundDecoratorFn";
	return Decorator;
}

export function enDateToTimeAgoDecorator(): DecoratorFunction<ReactRenderer> {
	TimeAgo.addLocale(enTimeAgo);
	const Decorator = (Story: PartialStoryFn<ReactRenderer>, context: StoryContext<ReactRenderer>): React.JSX.Element => (
		<Story {...context} />
	);
	Decorator.displayName = "enDateToTimeAgoDecorator";
	return Decorator;
}

export function jaDateToTimeAgoDecorator(): DecoratorFunction<ReactRenderer> {
	TimeAgo.addLocale(jaTimeAgo);
	const Decorator = (Story: PartialStoryFn<ReactRenderer>, context: StoryContext<ReactRenderer>): React.JSX.Element => (
		<Story {...context} />
	);
	Decorator.displayName = "jaDateToTimeAgoDecorator";
	return Decorator;
}

export function frDateToTimeAgoDecorator(): DecoratorFunction<ReactRenderer> {
	TimeAgo.addLocale(frTimeAgo);
	const Decorator = (Story: PartialStoryFn<ReactRenderer>, context: StoryContext<ReactRenderer>): React.JSX.Element => (
		<Story {...context} />
	);
	Decorator.displayName = "frDateToTimeAgoDecorator";
	return Decorator;
}

const initMessages = {
	"epic.cosmos.showmore": "Show more",
	"epic.cosmos.showless": "Show less",
	"epic.cosmos.view.next": "View next",
	"epic.cosmos.view.previous": "View previous",
	"epic.cosmos.ago": "ago",
	"epic.cosmos.postsPerWeek": "posts / week",
	"epic.cosmos.read.more": "Read more",
	"epic.cosmos.newsletter.subscribe": "Subscribe",
	"epic.cosmos.newsletter.signin": "Sign in to subscribe",
	"epic.cosmos.newsletter.industry.required": "Industry selection is required",
	"epic.cosmos.newsletter.not.allowed.title": "You are unable to sign up for the newsletter at this time",
	"epic.cosmos.newsletter.not.allowed.message": "Watch this space for updates as they become available.",
	"epic.cosmos.newsletter.globaloptin":
		"By submitting your information, you are agreeing to receive news, surveys, and special offers from Epic Games.",
	"epic.cosmos.globaloptin.checkbox": "I would like to receive news, surveys, and special offers from Epic Games.",
	"epic.cosmos.required.field.text": "This field is required",
	"epic.cosmos.privacypolicy": "Privacy policy",
	"epic.cosmos.auth.title": "Epic Account Required",
	"epic.cosmos.auth.description": "The page you are trying to reach requires an Epic account.",
	"epic.cosmos.auth.login.button.label": "Log in now",
	"epic.cosmos.auth.signup.description": "Don't have an Epic Games account?",
	"epic.cosmos.auth.signup.link": "Sign up",
	"epic.cosmos.auth.back": "Go back",
	"epic.cosmos.label.continent.africa": "Africa",
	"epic.cosmos.label.continent.asia": "Asia",
	"epic.cosmos.label.continent.australia": "Australia",
	"epic.cosmos.label.continent.europe": "Europe",
	"epic.cosmos.label.continent.northAmerica": "North America",
	"epic.cosmos.label.continent.southAmerica": "South America",
	"epic.cosmos.plugins.latest.version": "Get the <a>latest version</a>",
	"epic.cosmos.os.windows": "Windows",
	"epic.cosmos.os.macos": "macOS",
	"epic.cosmos.os.linux": "Linux",
	"epic.cosmos.os": "Operating system",
	"epic.cosmos.plugins.download": "Download",
	"epic.cosmos.plugins.archived.versions": "Archived versions",
	"epic.cosmos.agreements": "Agreements",
	"epic.cosmos.publication": "Publication",
	"epic.cosmos.blob.filename": "File name",
	"epic.cosmos.blob.size": "Size",
	"epic.cosmos.blob.dateUploaded": "Date uploaded",
	"epic.cosmos.blob.download.header": "Link",
	"epic.cosmos.blob.download": "Download",
	"epic.cosmos.blob.dateLastUpdated": "Last updated",
	"epic.cosmos.plugins.learnMore": "Learn More",
	"epic.cosmos.plugins.tpsNotices": "TPS Notices",
	"epic.cosmos.plugins.releaseNotes": "Release Notes",
};

export const i18nDecoratorFactory = (
	locale: string,
	messages: any = initMessages,
): DecoratorFunction<ReactRenderer> => {
	const Decorator = (Story: PartialStoryFn<ReactRenderer>, context: StoryContext<ReactRenderer>): React.JSX.Element => (
		<I18NProvider locale={locale} locales={[locale]} defaultLocale={locale} messages={messages}>
			<Story {...context} />
		</I18NProvider>
	);
	Decorator.displayName = "i18nDecoratorFactory";
	return Decorator;
};

export function i18nDecorator<T>(): DecoratorFunction<ReactRenderer, T> {
	const Decorator = (
		Story: PartialStoryFn<ReactRenderer, T>,
		context: StoryContext<ReactRenderer, T>,
	): React.JSX.Element => (
		<I18NProvider locale="en-US" locales={["en-US"]} defaultLocale="en-US" messages={initMessages}>
			<Story {...context} />
		</I18NProvider>
	);
	Decorator.displayName = "i18nDecorator";
	return Decorator;
}

export function locationDecorator<T>(): DecoratorFunction<ReactRenderer, T> {
	const Decorator = (
		Story: PartialStoryFn<ReactRenderer, T>,
		context: StoryContext<ReactRenderer, T>,
	): React.JSX.Element => (
		<LocationProvider location={new URL("http://localhost:6006/")}>
			<Story {...context} />
		</LocationProvider>
	);
	Decorator.displayName = "locationDecorator";
	return Decorator;
}

export function accountDecorator<T>(): DecoratorFunction<ReactRenderer, T> {
	const Decorator = (
		Story: PartialStoryFn<ReactRenderer, T>,
		context: StoryContext<ReactRenderer, T>,
	): React.JSX.Element => {
		const account = {
			isLoggedIn: true,
			country: "US",
			displayName: "Stormblessed",
			email: "kal@bridgecrew.com",
			id: "1234567890",
		};
		return (
			<AccountProvider initialValue={account}>
				<Story {...context} />
			</AccountProvider>
		);
	};
	Decorator.displayName = "accountDecorator";
	return Decorator;
}

export const nextRouterDecorator: DecoratorFunction<ReactRenderer> = (
	Story: PartialStoryFn<ReactRenderer>,
	context: StoryContext<ReactRenderer>,
) => (
	<AppRouterContext.Provider value={{} as AppRouterInstance}>
		<Story {...context} />
	</AppRouterContext.Provider>
);

export const withScreenshot = withScreenshot$1 as DecoratorFunction<ReactRenderer, any>;

export function decorators<T>(): DecoratorFunction<ReactRenderer, T>[] {
	return [
		withScreenshot,
		i18nDecorator(),
		edsProviderDecorator(),
		backgroundColorDecorator(),
		locationDecorator(),
		accountDecorator(),
	];
}
